import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_a913e632 from 'nuxt_plugin_sentryserver_a913e632' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_45bc9b5f from 'nuxt_plugin_sentryclient_45bc9b5f' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_templatesplugin8ade88ec_7c9f762a from 'nuxt_plugin_templatesplugin8ade88ec_7c9f762a' // Source: ./templates.plugin.8ade88ec.js (mode: 'client')
import nuxt_plugin_templatesplugin64927637_5b02394e from 'nuxt_plugin_templatesplugin64927637_5b02394e' // Source: ./templates.plugin.64927637.js (mode: 'client')
import nuxt_plugin_templatesplugin52cf1106_7be6484d from 'nuxt_plugin_templatesplugin52cf1106_7be6484d' // Source: ./templates.plugin.52cf1106.js (mode: 'client')
import nuxt_plugin_cookieuniversalnuxt_7687cc6a from 'nuxt_plugin_cookieuniversalnuxt_7687cc6a' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_pluginutils_47598f10 from 'nuxt_plugin_pluginutils_47598f10' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_6b30f07a from 'nuxt_plugin_pluginrouting_6b30f07a' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_1e75328c from 'nuxt_plugin_pluginmain_1e75328c' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_axios_826dbfba from 'nuxt_plugin_axios_826dbfba' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_axios_673c6d87 from 'nuxt_plugin_axios_673c6d87' // Source: ../plugins/axios.js (mode: 'all')
import nuxt_plugin_s3utils_cb7601a6 from 'nuxt_plugin_s3utils_cb7601a6' // Source: ../plugins/s3-utils.js (mode: 'all')
import nuxt_plugin_global_41b1cc6e from 'nuxt_plugin_global_41b1cc6e' // Source: ../plugins/global.js (mode: 'all')
import nuxt_plugin_featureflags_7df84666 from 'nuxt_plugin_featureflags_7df84666' // Source: ../plugins/feature-flags.js (mode: 'all')
import nuxt_plugin_debounce_1703d188 from 'nuxt_plugin_debounce_1703d188' // Source: ../plugins/debounce.js (mode: 'client')
import nuxt_plugin_googleanalytics_0123003f from 'nuxt_plugin_googleanalytics_0123003f' // Source: ../plugins/google-analytics.js (mode: 'client')
import nuxt_plugin_stananalytics_5787b8ca from 'nuxt_plugin_stananalytics_5787b8ca' // Source: ../plugins/stan-analytics.js (mode: 'client')
import nuxt_plugin_notification_ad5082b4 from 'nuxt_plugin_notification_ad5082b4' // Source: ../plugins/notification.js (mode: 'client')
import nuxt_plugin_webvitals_2ae1cf71 from 'nuxt_plugin_webvitals_2ae1cf71' // Source: ../plugins/web-vitals.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1,  maximum-scale=1.0, user-scalable=no"},{"hid":"og:title","property":"og:title","content":"Stan | Monetize Your Content"},{"hid":"og:image","property":"og:image","content":" https:\u002F\u002Fassets.stanwith.me\u002Fgraphics\u002Fstan-url-preview.jpg"},{"hid":"og:description","property":"og:description","content":"We work 1-on-1 with the Top Creators to monetize their business."},{"hid":"og:type","property":"og:type","content":"website"},{"hid":"og:site_name","property":"og:site_name","content":"stan.store"},{"hid":"twitter:card","name":"twitter:card","content":"summary_large_image"},{"hid":"description","name":"description","content":"Make Money Creating Stan profile"},{"hid":"twitter:title","name":"twitter:title","content":"Stan"},{"hid":"twitter:description","name":"twitter:description","content":"Make Money Creating Stan profile"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"},{"rel":"stylesheet","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss2?family=Nunito+Sans:wght@200;300;400;500;600;700;800;900&display=swap"}],"script":[{"src":"https:\u002F\u002Fmaps.googleapis.com\u002Fmaps\u002Fapi\u002Fjs?key=AIzaSyAjpZDyGwZdJHHCMQZ8CSRz3Si3qjQOrqU&libraries=places"}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_a913e632 === 'function') {
    await nuxt_plugin_sentryserver_a913e632(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_45bc9b5f === 'function') {
    await nuxt_plugin_sentryclient_45bc9b5f(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_templatesplugin8ade88ec_7c9f762a === 'function') {
    await nuxt_plugin_templatesplugin8ade88ec_7c9f762a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_templatesplugin64927637_5b02394e === 'function') {
    await nuxt_plugin_templatesplugin64927637_5b02394e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_templatesplugin52cf1106_7be6484d === 'function') {
    await nuxt_plugin_templatesplugin52cf1106_7be6484d(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_7687cc6a === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_7687cc6a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_47598f10 === 'function') {
    await nuxt_plugin_pluginutils_47598f10(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_6b30f07a === 'function') {
    await nuxt_plugin_pluginrouting_6b30f07a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_1e75328c === 'function') {
    await nuxt_plugin_pluginmain_1e75328c(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_826dbfba === 'function') {
    await nuxt_plugin_axios_826dbfba(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_673c6d87 === 'function') {
    await nuxt_plugin_axios_673c6d87(app.context, inject)
  }

  if (typeof nuxt_plugin_s3utils_cb7601a6 === 'function') {
    await nuxt_plugin_s3utils_cb7601a6(app.context, inject)
  }

  if (typeof nuxt_plugin_global_41b1cc6e === 'function') {
    await nuxt_plugin_global_41b1cc6e(app.context, inject)
  }

  if (typeof nuxt_plugin_featureflags_7df84666 === 'function') {
    await nuxt_plugin_featureflags_7df84666(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_debounce_1703d188 === 'function') {
    await nuxt_plugin_debounce_1703d188(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googleanalytics_0123003f === 'function') {
    await nuxt_plugin_googleanalytics_0123003f(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_stananalytics_5787b8ca === 'function') {
    await nuxt_plugin_stananalytics_5787b8ca(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_notification_ad5082b4 === 'function') {
    await nuxt_plugin_notification_ad5082b4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_webvitals_2ae1cf71 === 'function') {
    await nuxt_plugin_webvitals_2ae1cf71(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
